import { DISCOUNT_TYPE, RECEIPT_PAYMENT_MODE_ID } from "@elixir/types";
import dayjs from "dayjs";
import useGetReceiptConfig from "../../../apis/tryst/get-receipt-config";
import { ReactComponent as LoadingCircleIcon } from "../../../assets/icons/custom/loading-circle.svg";
import useBillingStore from "../../../stores/billing-store/billing-store";
import useReceiptCustomisationStore from "../../../stores/receipt-customisation/receipt-customisation-store";
import {
  getBody,
  getFooter,
  getHeader,
  getPdfCss,
} from "../../../utils/pdf-utils";
import { useMyAccessProfileV2 } from "../../../apis/tryst/get-my-access-profile";
import {
  getMe,
  returnActorNamePerformingWithPersona,
} from "../../../utils/bridges/methods";
import {
  Actor,
  MAP_PERSONA_TO_ACTOR_PREFIX,
} from "../../../controllers/appointments/appointment-activity";
import useGetQueueConfig from "../../../apis/tryst/get-queue-config";

const ReceiptPdf = () => {
  const {
    columns,
    footer: footerCustomisation,
    header: headerCustomisation,
    margin,
    others,
    clinicId,
    selectedDoctorId, // Add this
  } = useReceiptCustomisationStore();

  const { data: myAccessProfile } = useMyAccessProfileV2();

  const { clinicId: cId = myAccessProfile?.clinics?.[0]?.id || "" } =
    useBillingStore();

  // Use selectedDoctorId from store, fallback to myAccessProfile
  const currentDoctorId =
    selectedDoctorId || myAccessProfile?.loginmeta?.docid!;
  const currentClinicId = clinicId || cId;

  const { isLoading } = useGetReceiptConfig({
    clinic_id: currentClinicId,
    doctor_oid: currentDoctorId, // Use selected doctor
  });

  const { data: queueConfigData } = useGetQueueConfig();
  const { data: queueConfig } = queueConfigData || {};
  const { receipt_counter } = queueConfig || {};

  const currentReceiptCounter = receipt_counter?.find(
    (rc) => rc?.clinic_id == currentClinicId
  );

  const receiptPrefix = currentReceiptCounter?.receipt_prefix || "EK-";
  const receiptNumber = currentReceiptCounter?.receipt_counter || 1;
  const displayReceiptNumber = `${receiptPrefix}${receiptNumber}`;

  const clinic = myAccessProfile?.clinics?.find(
    (clinic) => clinic.id === currentClinicId
  );
  const header = getHeader({
    clinicAddress: "Street Name, Area Name, City, Country",
    clinicName: clinic?.name || "",
    doctorName: "Dr Name",
    headerText: "Header Text",
    headerCustomisation,
  });

  const body = getBody({
    data: {
      config: {
        columns,
        footer: footerCustomisation,
        header: headerCustomisation,
        margin,
        others,
      },
      additional_discount_amount: 9,
      additional_discount_type: DISCOUNT_TYPE.PERCENTAGE,
      additional_discount_value: 10,
      visit_date: dayjs().toISOString(),
      mobile: "+911243434340",
      name: "Manvendar Singh",
      net_amount: 90,
      receipt_amount: 90,
      receipt_id: 1223,
      receipt_number: displayReceiptNumber,
      receipt_payment: [
        {
          amount: 60,
          paymode: RECEIPT_PAYMENT_MODE_ID.CASH,
          receipt_id: 1223,
          receipt_payment_id: 1,
        },
        {
          amount: 30,
          paymode: RECEIPT_PAYMENT_MODE_ID.GPAY,
          receipt_id: 1223,
          receipt_payment_id: 1,
        },
      ],
      receipt_sku: [
        {
          quantity: 1,
          amount: 100,
          discount_value: 0,
          discount_type: "AMOUNT",
          discount_amount: 0,
          net_amount: 100,
          sku_item: {
            service_name: "consultation",
            service_id: 1,
            price: 100,
          },
          price: 100,
          service_id: 1,
          service_name: "consultation",
        },
      ],
      uhid: "EKEPK204A",
    },
  });

  const createdByName = returnActorNamePerformingWithPersona();
  const footer = getFooter({
    footerCustomisation: {
      ...footerCustomisation,
      print_bill_created_by: others.print_bill_created_by,
      createdByName,
    },
  });

  const pdfCss = getPdfCss({
    columns,
    footer: footerCustomisation,
    header: headerCustomisation,
    margin,
    others,
  });

  if (isLoading) {
    return (
      <div className="flex dr-h-full dr-w-full justify-center items-center">
        <LoadingCircleIcon className="h-24 w-24 text-neutral-1000" />
      </div>
    );
  }

  return (
    <div
      style={{
        width: columns.page_size === "A4" ? "21cm" : "14.8cm",
        height: columns.page_size === "A4" ? "29.7cm" : "21cm",
        scale: "0.7",
        backgroundColor: "white",
        transformOrigin: "5% 1.5%",
      }}
    >
      <div
        id="receipt_html"
        className="relative overflow-hidden h-full w-full"
        dangerouslySetInnerHTML={{
          __html: `
        ${pdfCss}
            ${headerCustomisation.showHeader ? header : ""}
           ${body}
            ${footerCustomisation?.showFooter ? footer : ""}
         `,
        }}
      />
    </div>
  );
};

export default ReceiptPdf;
